/**
 * Archivo: Cabecera.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 20/08/2024
 * 
 * Descripción:
 * Este componente representa la cabecera de la aplicación, que incluye el logo y un botón para cerrar sesión para pantallas grandes
 * y también contiene un botón de menú desplegable e caso de pantallas pequeñas, dónde aparecen todas las opciones de la segunda cabecera.
 * Además, se incluye un indicador de notificaciones nuevas para usuarios con el rol adecuado.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - react-bootstrap: ^2.5.0
 * - js-cookie: ^3.0.1
 * - react-router-dom: ^6.4.0
 * 
 * Propósito:
 * Proporcionar una interfaz de usuario para la cabecera de la aplicación, incluyendo la funcionalidad de cerrar sesión,
 * acceder a notificaciones y navegar a diferentes secciones de la aplicación según el rol del usuario.
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import '../Estilos/estilos.css';
import { getMensajesNuevos } from '../Servicios/MensajesServicios';

import logoBlanco from '../Imagenes/Iconos/LogoBlanco.png';
import cerrarSesionBlanco from '../Imagenes/Iconos/CerrarSesionBlanco.png';
import menuBlanco from '../Imagenes/Iconos/MenuBlanco.png';
import notificacionesBlanco from '../Imagenes/Iconos/NotificacionesBlanco.png';

const Cabecera = ({ permisos }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const cerrarSesion = async (event) => {
        Cookies.remove('token');
        Cookies.remove('permisos');
        navigate(process.env.PUBLIC_URL + '/InicioSesion');
    };

    const [mostrarModalCerrarSesion, setMostrarModalCerrarSesion] = useState(false);
    const [mostrarMenu, setMostrarMenu] = useState(false);
    const [mensajesNuevos, setMensajesNuevos] = useState(0);

    const irNotificaciones = () => {
        navigate(process.env.PUBLIC_URL + '/Notificaciones');
    };
    const handleCerrarSesion = () => {
        setMostrarModalCerrarSesion(true);
    };
    const handleCloseModalCerrarSesion = () => {
        setMostrarModalCerrarSesion(false);
    };
    const toggleMenu = () => {
        setMostrarMenu(!mostrarMenu);
    };
    useEffect(() => {
        const fetchData = async () => {
            const token = Cookies.get('token');
            
            const result = await getMensajesNuevos(token);
            if (result.success) {
                setMensajesNuevos(result.data);
            }
        };
        fetchData();
    }, []);

    const irRuta = (ruta) => {
        navigate(process.env.PUBLIC_URL + ruta);
        setMostrarMenu(false);
    };

    const opcionesMenu = [
        { nombre: 'Orla colegial', ruta: '/OrlaColegial' },
        { nombre: 'Boletín semanal', ruta: '/Boletines' },
        { nombre: 'Menú comedor', ruta: '/MenusComedor' },
        { nombre: 'Comunicados oficiales', ruta: '/ComunicadosOficiales' },
        { permisos: 'Colegial', nombre: 'Pase de comidas', ruta: '/PaseComidas' },
        { permisos: 'Conserjería', nombre: 'Mensajería', ruta: '/Mensajeria' },
        { permisos: 'Administrador', nombre: 'Usuarios', ruta: '/UsuariosAdmin' },
        { permisos: 'Administrador', nombre: 'Ajustes', ruta: '/AjustesAdmin' }
    ];

    return (
        <>
            <div className="container-fluid" style={{ borderBottom: '2px solid black' }}>
                <div className="row justify-content-between p-0" style={{ backgroundColor: 'black', height: '3rem' }}>
                    <div className="col-8 d-flex">
                        <img
                            alt="login-icon"
                            className="img mt-2"
                            src={logoBlanco}
                            style={{ height: '2rem', width: '8rem' }}
                        />
                    </div>
                    <div className='col-3 justify-content-end d-flex d-md-none'>
                        <div className={`mt-2 col-2 d-flex justify-content-center ${location.pathname.includes('/Notificaciones') ? 'active' : ''}`} onClick={irNotificaciones}>
                            <img
                                alt="notificaciones"
                                className="img mt-0"
                                src={notificacionesBlanco}
                                style={{ height: '2rem', width: '2rem', cursor: 'pointer' }}
                            />
                            {mensajesNuevos > 0 && (
                                <span className="badge badge-danger position-absolute" style={{ top: 0, right: 0 }}>
                                    {mensajesNuevos}
                                </span>
                            )}
                        </div>
                        <div className='ml-2'>
                            <img
                                alt="menu-icon"
                                className="img mt-2"
                                src={menuBlanco}
                                style={{ height: '2rem', width: '2rem', cursor: 'pointer' }}
                                onClick={toggleMenu}
                            />
                        </div>
                    </div>
                    <div className='col-1 justify-content-end d-flex'>
                        <img
                            alt="cerrar-sesion"
                            className="img mt-2"
                            src={cerrarSesionBlanco}
                            style={{ height: '2rem', width: '2rem', cursor: 'pointer' }}
                            onClick={handleCerrarSesion}
                        />
                    </div>
                </div>
                <Modal show={mostrarModalCerrarSesion} onHide={handleCloseModalCerrarSesion} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>¿Quieres cerrar sesión?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Esta acción cerrará tu sesión actual. ¿Estás seguro?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModalCerrarSesion}>
                            No
                        </Button>
                        <Button variant="primary" onClick={cerrarSesion}>
                            Sí
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
            {mostrarMenu && (
                <div className="overlay-menu">
                    <div className="overlay-content">
                        {opcionesMenu.map((opcion, index) => (
                            (opcion.permisos === undefined || opcion.permisos === permisos) && (
                                <div key={index} className={`overlay-item ${opcion.className || ''}`} onClick={() => irRuta(opcion.ruta)}>
                                    {opcion.nombre}
                                </div>
                            )
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};
export default Cabecera;
